import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Plus, RefreshCw } from "lucide-react";
import AddressAutocomplete from "@/components/AddressAutocomplete";

const STATUS_COLORS = {
  pending: "bg-amber-400/20 text-amber-300 border-amber-400/40",
  assigned: "bg-blue-400/20 text-blue-300 border-blue-400/40",
  in_progress: "bg-violet-400/20 text-violet-300 border-violet-400/40",
  completed: "bg-emerald-400/20 text-emerald-300 border-emerald-400/40",
  cancelled: "bg-red-400/20 text-red-300 border-red-400/40",
};

export default function BookingsTab({ isSuper }) {
  const [bookings, setBookings] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openManual, setOpenManual] = useState(false);
  const [corpOnly, setCorpOnly] = useState(false);
  const [manual, setManual] = useState({
    rider_name: "", rider_mobile: "", rider_email: "",
    pickup_location: "", dropoff_location: "",
    pickup_type: "asap", pickup_time: "",
    passenger_count: 1, notes: "",
    service_id: "", estimated_distance_km: 10, source: "manual",
    assign_driver_id: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const [b, d, s] = await Promise.all([
        api.get(`/bookings${corpOnly ? "?corporate_only=true" : ""}`),
        api.get("/drivers"), api.get("/services")
      ]);
      setBookings(b.data); setDrivers(d.data); setServices(s.data);
    } catch { toast.error("Failed to load bookings"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [corpOnly]); // eslint-disable-line

  const updateBooking = async (id, patch) => {
    try {
      await api.patch(`/bookings/${id}`, patch);
      toast.success("Booking updated"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const submitManual = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...manual, passenger_count: Number(manual.passenger_count), estimated_distance_km: Number(manual.estimated_distance_km) };
      if (!payload.rider_email) delete payload.rider_email;
      if (payload.pickup_type === "asap") payload.pickup_time = null;
      if (!payload.service_id) delete payload.service_id;
      if (!payload.assign_driver_id) delete payload.assign_driver_id;
      const { data } = await api.post("/bookings", payload);
      toast.success(`Manual booking created — ${data.reference}${payload.assign_driver_id ? " · driver assigned" : ""}`);
      setOpenManual(false);
      setManual({ ...manual, rider_name: "", rider_mobile: "", rider_email: "", pickup_location: "", dropoff_location: "", notes: "", assign_driver_id: "", pickup_type: "asap", pickup_time: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <Header title="Bookings" subtitle="Live dispatch queue">
        <label className="flex items-center gap-2 cursor-pointer text-xs font-mono uppercase tracking-wider text-slate-400 hover:text-amber-400" data-testid="bookings-corp-only-label">
          <input type="checkbox" checked={corpOnly} onChange={(e) => setCorpOnly(e.target.checked)} data-testid="bookings-corp-only-toggle" className="accent-amber-400" />
          Corporate only
        </label>
        <Button variant="outline" onClick={load} className="bg-transparent border-white/15 text-white hover:bg-white/5 rounded-full" data-testid="bookings-refresh"><RefreshCw className="w-4 h-4 mr-2" /> Refresh</Button>
        <Dialog open={openManual} onOpenChange={setOpenManual}>
          <DialogTrigger asChild>
            <Button className="btn-gold rounded-full" data-testid="bookings-add-manual"><Plus className="w-4 h-4 mr-2" /> Manual booking</Button>
          </DialogTrigger>
          <DialogContent className="bg-[#111827] border-white/10 text-white max-w-2xl">
            <DialogHeader><DialogTitle className="font-display">Manual booking (phone)</DialogTitle></DialogHeader>
            <form onSubmit={submitManual} className="grid sm:grid-cols-2 gap-4">
              {[
                ["rider_name", "Name *"], ["rider_mobile", "Mobile *"],
                ["rider_email", "Email"],
              ].map(([k, l]) => (
                <div key={k}><Label className="text-xs uppercase text-slate-400">{l}</Label>
                  <Input required={l.endsWith("*")} value={manual[k]} onChange={(e) => setManual({ ...manual, [k]: e.target.value })} className="bg-black/30 border-white/10 mt-1 h-10" /></div>
              ))}
              <div className="sm:col-span-2"><Label className="text-xs uppercase text-slate-400">Pickup *</Label>
                <AddressAutocomplete
                  data-testid="manual-pickup"
                  value={manual.pickup_location}
                  onChange={(v) => setManual((m) => ({ ...m, pickup_location: v }))}
                  placeholder="Search pickup address…"
                  className="bg-black/30 border-white/10 mt-1 h-10"
                /></div>
              <div className="sm:col-span-2"><Label className="text-xs uppercase text-slate-400">Drop-off *</Label>
                <AddressAutocomplete
                  data-testid="manual-dropoff"
                  value={manual.dropoff_location}
                  onChange={(v) => setManual((m) => ({ ...m, dropoff_location: v }))}
                  placeholder="Search drop-off address…"
                  className="bg-black/30 border-white/10 mt-1 h-10"
                /></div>
              <div><Label className="text-xs uppercase text-slate-400">Service</Label>
                <Select value={manual.service_id} onValueChange={(v) => setManual({ ...manual, service_id: v })}>
                  <SelectTrigger className="bg-black/30 border-white/10 mt-1 h-10" data-testid="manual-service"><SelectValue placeholder="Auto" /></SelectTrigger>
                  <SelectContent className="bg-[#111827] border-white/10 text-white">
                    {services.filter(s => s.active).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select></div>
              <div><Label className="text-xs uppercase text-slate-400">Passengers</Label>
                <Input type="number" min="1" value={manual.passenger_count} onChange={(e) => setManual({ ...manual, passenger_count: e.target.value })} className="bg-black/30 border-white/10 mt-1 h-10" /></div>
              <div><Label className="text-xs uppercase text-slate-400">Distance (km)</Label>
                <Input type="number" min="1" value={manual.estimated_distance_km} onChange={(e) => setManual({ ...manual, estimated_distance_km: e.target.value })} className="bg-black/30 border-white/10 mt-1 h-10" /></div>

              {/* Pickup timing: Ride now vs Schedule */}
              <div className="sm:col-span-2 pt-1">
                <Label className="text-xs uppercase text-slate-400">When</Label>
                <div className="flex gap-2 mt-1.5">
                  {[
                    { v: "asap", label: "Ride now" },
                    { v: "scheduled", label: "Schedule for later" },
                  ].map((o) => (
                    <button
                      key={o.v}
                      type="button"
                      onClick={() => setManual({ ...manual, pickup_type: o.v })}
                      className={`flex-1 h-10 rounded-lg text-xs font-mono uppercase tracking-widest ${
                        manual.pickup_type === o.v
                          ? "bg-amber-400 text-[#090D14]"
                          : "bg-black/30 border border-white/10 text-slate-300 hover:text-white"
                      }`}
                      data-testid={`manual-when-${o.v}`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
                {manual.pickup_type === "scheduled" && (
                  <Input
                    type="datetime-local"
                    required
                    value={manual.pickup_time || ""}
                    onChange={(e) => setManual({ ...manual, pickup_time: e.target.value })}
                    className="bg-black/30 border-white/10 mt-2 h-10 font-mono"
                    data-testid="manual-pickup-time"
                  />
                )}
              </div>

              {/* Optional driver auto-assign */}
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase text-slate-400">Assign driver now (optional)</Label>
                <Select value={manual.assign_driver_id} onValueChange={(v) => setManual({ ...manual, assign_driver_id: v })}>
                  <SelectTrigger className="bg-black/30 border-white/10 mt-1 h-10" data-testid="manual-assign-driver"><SelectValue placeholder="Leave unassigned" /></SelectTrigger>
                  <SelectContent className="bg-[#111827] border-white/10 text-white">
                    <SelectItem value="none">— Leave unassigned —</SelectItem>
                    {drivers.filter(d => d.status !== "offline").map((d) => (
                      <SelectItem key={d.id} value={d.id}>{d.name} · {d.vehicle} {d.plate ? `(${d.plate})` : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-slate-500 mt-1">When set, booking goes straight to <b>assigned</b> and the driver gets a heads-up.</p>
              </div>
              <div className="sm:col-span-2"><Label className="text-xs uppercase text-slate-400">Notes</Label>
                <Textarea value={manual.notes} onChange={(e) => setManual({ ...manual, notes: e.target.value })} rows={2} className="bg-black/30 border-white/10 mt-1" /></div>
              <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setOpenManual(false)} className="bg-transparent border-white/15 hover:bg-white/5 text-white">Cancel</Button>
                <Button type="submit" className="btn-gold">Create booking</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </Header>

      <div className="glass-card rounded-2xl border border-white/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03] text-xs uppercase tracking-wider text-slate-400">
              <tr>
                {["Ref", "Rider", "Route", "When", "Status", "Driver", "Fare", "Payment", "Actions"].map(h => (
                  <th key={h} className="text-left px-4 py-3 font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bookings.length === 0 && !loading && (
                <tr><td colSpan="9" className="text-center py-12 text-slate-500">No bookings yet.</td></tr>
              )}
              {bookings.map((b) => (
                <tr key={b.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`booking-row-${b.reference}`}>
                  <td className="px-4 py-3 font-mono text-amber-400 font-semibold">
                    #{b.reference}
                    {b.corporate_name && (
                      <div className="mt-1 inline-flex items-center gap-1 rounded-full border border-indigo-400/40 bg-indigo-400/10 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider text-indigo-300" data-testid={`booking-corp-${b.reference}`}>
                        <span className="w-1 h-1 rounded-full bg-indigo-400" /> {b.corporate_name}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium">{b.rider_name}</div>
                    <div className="text-xs text-slate-500">{b.rider_mobile}</div>
                  </td>
                  <td className="px-4 py-3 max-w-xs">
                    <div className="text-slate-300 truncate">{b.pickup_location}</div>
                    <div className="text-xs text-slate-500 truncate">→ {b.dropoff_location}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-400">{b.pickup_time_display}</td>
                  <td className="px-4 py-3">
                    <Badge className={`${STATUS_COLORS[b.status]} border font-mono uppercase text-[10px] tracking-wider`}>{b.status.replace("_", " ")}</Badge>
                  </td>
                  <td className="px-4 py-3">
                    <Select value={b.driver_id || ""} onValueChange={(v) => updateBooking(b.id, { driver_id: v, status: b.status === "pending" ? "assigned" : b.status })}>
                      <SelectTrigger className="bg-black/30 border-white/10 h-9 w-40" data-testid={`booking-assign-${b.reference}`}><SelectValue placeholder="Assign" /></SelectTrigger>
                      <SelectContent className="bg-[#111827] border-white/10 text-white">
                        {drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-3 font-mono">NZ$ {Number(b.fare_estimate).toFixed(2)}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs uppercase font-mono tracking-wider ${b.payment_status === "paid" ? "text-emerald-400" : "text-slate-500"}`}>{b.payment_status}</span>
                  </td>
                  <td className="px-4 py-3">
                    <Select value="" onValueChange={(v) => updateBooking(b.id, { status: v })}>
                      <SelectTrigger className="bg-black/30 border-white/10 h-9 w-36" data-testid={`booking-status-${b.reference}`}><SelectValue placeholder="Status →" /></SelectTrigger>
                      <SelectContent className="bg-[#111827] border-white/10 text-white">
                        {["pending", "assigned", "in_progress", "completed", "cancelled"].map(s =>
                          <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export const Header = ({ title, subtitle, children }) => (
  <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
    <div>
      <div className="text-caption mb-2">Admin</div>
      <h1 className="text-3xl font-bold font-display">{title}</h1>
      {subtitle && <p className="text-slate-400 mt-1 text-sm">{subtitle}</p>}
    </div>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);
